import { Loader2, Save } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface UnsavedChangesDialogProps {
  /** Controls whether the warning dialog is visible. */
  open: boolean;
  /** Called when the dialog open state should change (e.g. Cancel or Escape). */
  onOpenChange: (open: boolean) => void;
  /**
   * Persist unsaved work. May return a promise; the dialog stays open until the
   * caller closes it after a successful save. Rejections are swallowed here so
   * callers control error UI without unhandled promise rejections.
   */
  onSave: () => void | Promise<void>;
  /** Discard unsaved work (typically also closes the parent editor). */
  onDiscard: () => void;
  /** When true, disables all actions and shows a saving state on Save. */
  isSaving?: boolean;
  /** Optional title override. Defaults to "Unsaved changes". */
  title?: string;
  /** Optional description override. */
  description?: string;
}

/**
 * Three-action warning for leaving an editor with unsaved changes.
 *
 * Renders Cancel (keep editing), Lose Changes (discard), and Save (persist).
 * Save does not auto-close the dialog so callers can await persistence and
 * close only on success.
 *
 * @param props - {@link UnsavedChangesDialogProps}
 */
export function UnsavedChangesDialog({
  open,
  onOpenChange,
  onSave,
  onDiscard,
  isSaving = false,
  title = "Unsaved changes",
  description = "You have unsaved changes that will be lost if you leave. Save them first, discard them, or cancel to keep editing.",
}: UnsavedChangesDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="outline"
            disabled={isSaving}
            onClick={onDiscard}
          >
            Lose Changes
          </AlertDialogAction>
          <AlertDialogAction
            disabled={isSaving}
            onClick={(event) => {
              event.preventDefault();
              void Promise.resolve(onSave()).catch(() => {
                // Caller owns error UI; keep the dialog open on failure.
              });
            }}
          >
            {isSaving ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="size-4" />
                Save
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
